
import { defineComponent, reactive, ref, watch, computed, PropType } from 'vue'
import { ElForm } from 'element-plus'
import { useRouter } from 'vue-router'

import JrText from '@/components/jr-parts/JrText.vue'
import JrDateTimePicker from '@/components/jr-parts/JrDateTimePicker.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import JrButtonForJr from '@/components/jr-parts/JrButtonForJr.vue'
import JrCheckbox from '@/components/jr-parts/JrCheckbox.vue'
import JrGeocoder from '@/components/jr-parts/JrGeocoder.vue'
import JrNumber from '@/components/jr-parts/JrNumber.vue'
import JrMap from '@/components/jr-parts/JrMap.vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'
import JrFileView from '@/components/jr-parts/JrFileView.vue'
import InputKilo from '@/components/inputs/InputKilo.vue'
import GeneralAttachedFiles from '@/components/inputs/GeneralAttachedFiles.vue'
import GeneralAttachedFilesView from '@/components/inputs/GeneralAttachedFilesView.vue'
import DepartmentSelect from '@/components/inputs/DepartmentSelect.vue'
import DirectionSelect from '@/components/inputs/DirectionSelect.vue'
import LineSelect from '@/components/inputs/LineSelect.vue'
import StationSelect from '@/components/inputs/StationSelect.vue'
import RequestReasonTypeSelect from '@/components/inputs/RequestReasonTypeSelect.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import InputGroup from '@/components/layout/InputGroup.vue'
import FormItemDisabled from '@/components/layout/FormItemDisabled.vue'
import CollapseGroup from '@/components/layout/CollapseGroup.vue'
import MessageHistoryToMessageType from '@/components/others/MessageHistoryToMessageType.vue'
import BoundarySaveConfirmDialog from '@/components/dialogs/BoundarySaveConfirmDialog.vue'

import { RequestHeaderBoundaryDetailForm } from '@/types/forms'
import { FileTypes, RequestTypes, RequestReasonTypes, APPLICANT_UPLOAD_FILE_EXTS } from '@/constants/enums'
import { ReadRequest } from '@/requests/requestHeader'
import { DEFAULT_CENTER_LAT, DEFAULT_CENTER_LNG, DEFAULT_ZOOM, PIN_ZOOM } from '@/constants/map'
import Loggers from '@/services/logger'
import { boundaryEditRule } from '@/constants/rules'
import { MessageTypes } from '@/constants/messageTypes'
import MessageService from '@/services/message'
import { addMessageQueue, MESSAGE_TYPE_TOAST } from '@/plugins/messageQueue'

/**
 * 動作モード
 * view: 表示
 * edit: 編集
 * add: 新規追加
 * detail: 詳細
 */
type Mode = 'view' | 'edit' | 'add' | 'detail'

//履歴表示対象のメッセージ種別
const HISTORY_MESSAGE_TYPES = Object.freeze([
  MessageTypes.B_APPLICANT_CALL,
  MessageTypes.B_APPLICANT_CALL_RECV,
  MessageTypes.B_APPLICANT_CALL_SENDER_CALL,
  MessageTypes.APPLICANT_REGIST_BOUNDARY,
  MessageTypes.CHANGE_BOUNDARY_NOTICE,
  MessageTypes.PROXY_REGIST_BOUNDARY_NOTICE,
] as number[])

const blankRequestHeader: RequestHeaderBoundaryDetailForm = Object.freeze({
  boundary: Object.freeze({
    agent_address: '',
    agent_corporate_name: '',
    agent_name: '',
    contact_tel: '',
    request_reason_type: -1,
    request_reason_type_other: '',
    is_boundary_marker_confirm: false,
    is_boundary_marker_restore: false,
    is_boundary_fixed_surveying: false,
    location: '',
    parcel_number: '',
    land_category: '',
    parcel_area: '',
    line: null,
    line_name: '',
    start_station: null,
    end_station: null,
    start_station_name: '',
    end_station_name: '',
    latitude: '',
    longitude: '',
    jr_site_location: '',
    jr_site_parcel_number: '',
    request_header: 1,
    boundary_application_form_file: -1,
    // 本人確認写・委任状・地積測量図・現地写真はnull許可のため、nullで指定
    identification_file: null,
    power_of_attorney_file: null,
    location_map_file: -1,
    ground_plan_file: -1,
    survey_drawing_of_acreage_file: null,
    cadastral_map_file: -1,
    certificate_of_registered_matters_file: -1,
    actual_place_photo_file: null,
    work_content_count: 0,
    boundary_application_form_file_selected: false,
    identification_file_selected: false,
    power_of_attorney_file_selected: false,
    location_map_file_selected: false,
    ground_plan_file_selected: false,
    survey_drawing_of_acreage_file_selected: false,
    cadastral_map_file_selected: false,
    certificate_of_registered_matters_file_selected: false,
    actual_place_photo_file_selected: false,
  }),
  general_attached_file: [],
  request_type: RequestTypes.BOUNDARY,
  applicant_number: '',
  applicant_time: '',
  applicant_email: '',
  department_code: '',
  department_name: '',
  applicant_title: '',
  applicant_business_name: '',
  applicant_manager_name: '',
  applicant_address: '',
  applicant_tel: '',
  meter: undefined,
  direction: null,
  department: undefined,
  is_tanto_department_login_user: false,
})

/**
 * 境界立会情報
 * props.modeによりボタン等の表示切替を行う
 */
export default defineComponent({
  emits: ['update:form'],
  name: 'BoundaryInfo',
  components: {
    InputKilo,
    InputLabel,
    InputGroup,
    JrText,
    DepartmentSelect,
    JrDateTimePicker,
    JrButton,
    JrButtonForJr,
    DirectionSelect,
    JrNumber,
    LineSelect,
    StationSelect,
    RequestReasonTypeSelect,
    FormItemDisabled,
    JrCheckbox,
    JrGeocoder,
    JrMap,
    JrFileInput,
    JrFileView,
    GeneralAttachedFiles,
    GeneralAttachedFilesView,
    CollapseGroup,
    MessageHistoryToMessageType,
    BoundarySaveConfirmDialog,
  },
  props: {
    // 申請基本ID
    requestHeaderId: {
      type: Number,
      required: false,
      default: null,
    },
    // 動作モード
    mode: {
      type: String as PropType<Mode>,
      required: false,
      default: 'view',
    },
  },
  setup(props, context) {
    const router = useRouter()

    const formRef = ref<InstanceType<typeof ElForm>>()
    const generalAttachedFilesRef = ref<InstanceType<typeof GeneralAttachedFiles>>()
    const boundary_application_form_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const identification_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const power_of_attorney_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const location_map_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const ground_plan_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const survey_drawing_of_acreage_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const cadastral_map_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const certificate_of_registered_matters_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const actual_place_photo_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const fileRefs = [
      boundary_application_form_file_ref,
      identification_file_ref,
      power_of_attorney_file_ref,
      location_map_file_ref,
      ground_plan_file_ref,
      survey_drawing_of_acreage_file_ref,
      cadastral_map_file_ref,
      certificate_of_registered_matters_file_ref,
      actual_place_photo_file_ref,
    ]
    const form = reactive<RequestHeaderBoundaryDetailForm>({
      ...blankRequestHeader,
      boundary: { ...blankRequestHeader.boundary },
    })
    const mapCenterLat = ref<string>(DEFAULT_CENTER_LAT)
    const mapCenterLng = ref<string>(DEFAULT_CENTER_LNG)
    const mapZoom = ref<number>(DEFAULT_ZOOM)
    // GoogleMapを読み込むかどうか
    const mapLoad = ref<boolean>(false)
    const changeMapCenter = () => {
      mapZoom.value = PIN_ZOOM
      // 変更時は座標をリセットする
      form.boundary.latitude = ''
      form.boundary.longitude = ''
    }

    const validationError = ref<{ start_station?: string; end_station?: string }>({})

    watch(
      () => props.requestHeaderId,
      async (id) => {
        if (props.mode === 'add') {
          // 新規登録時は取得しない
          return
        }
        const request = new ReadRequest(id)
        if (await request.exec()) {
          if (request.getRequestType() !== RequestTypes.BOUNDARY) {
            // 近接工事では無ければ404へ飛ばす
            Loggers.info('申請種別が異なります。')
            router.push({ name: 'NotFound' })
            return
          }
          const requestHeader = request.getBoundary()
          // 取得した情報をformに入れる
          Object.assign(form, requestHeader)
          mapZoom.value = PIN_ZOOM
          mapCenterLat.value = requestHeader.boundary.latitude
          mapCenterLng.value = requestHeader.boundary.longitude
        }
      },
      { immediate: true }
    )

    // form変更時はemitする
    watch(
      () => form,
      () => {
        context.emit('update:form', form)
      },
      { deep: true }
    )

    // 開始駅・終了駅の監視
    watch(
      () => [form.boundary.start_station, form.boundary.end_station],
      () => {
        if ((form.boundary.start_station == null) === (form.boundary.end_station == null)) {
          // どちらも入力されたか、どちらも入力されていない場合エラーをクリアする
          validationError.value = {}
        }
      },
      { deep: true }
    )

    // 作業内容選択数の監視
    watch(
      () => [
        form.boundary.is_boundary_marker_confirm,
        form.boundary.is_boundary_marker_restore,
        form.boundary.is_boundary_fixed_surveying,
      ],
      (workContents) => {
        // trueの合計を調べる
        form.boundary.work_content_count = workContents.filter((content) => content).length
      },
      { deep: true }
    )

    // モード別動作
    // 編集可否 新規・編集は編集モード
    const isEdit = computed(() => props.mode === 'add' || props.mode === 'edit')
    // 戻り先
    const returnTo = computed(() => {
      if (props.mode === 'view' || props.mode === 'edit') {
        // 表示モード・編集モードは詳細画面に戻る
        return {
          name: 'BoundaryDetail',
          params: {
            request_header_id: props.requestHeaderId,
          },
        }
      } else {
        // 新規・詳細モードは一覧に戻る
        return {
          name: 'BoundaryList',
          query: {
            screen: props.mode, // モードをクエリストリングに付与
          },
        }
      }
    })
    // 変更ボタン表示 詳細画面のみ表示
    const isEditBtnVisible = computed(() => props.mode === 'detail')
    // 協議先連絡ボタン  詳細画面のみ表示
    const isApplicantContactBtnVisible = computed(() => props.mode === 'detail')
    // メッセージ履歴表示 詳細画面のみ表示
    const isMessageHistoryVisible = computed(() => props.mode === 'detail')

    const saveConfirmDialogVisible = ref<boolean>(false)
    const save = () => {
      if (!formRef.value) {
        return
      }
      formRef.value.validate((valid) => {
        if ((form.boundary.start_station == null) !== (form.boundary.end_station == null)) {
          // 開始駅・終了駅のどちらかのみ入力されている場合エラー
          if (form.boundary.start_station == null) {
            validationError.value.start_station = MessageService.bind(MessageService.codes.V_REQUIRED_SELECT_IF, {
              target: '終了駅',
              attribute: '開始駅',
            })
          } else {
            validationError.value.end_station = MessageService.bind(MessageService.codes.V_REQUIRED_SELECT_IF, {
              target: '開始駅',
              attribute: '終了駅',
            })
          }
          return
        } else {
          validationError.value = {}
        }
        if (valid) {
          saveConfirmDialogVisible.value = true
        }
      })
    }

    /**
     * 保存後処理
     */
    const saveSent = () => {
      if (props.mode === 'edit') {
        // 編集時は詳細に戻る
        router.push({
          name: 'BoundaryDetail',
          params: {
            request_header_id: props.requestHeaderId,
          },
        })
      } else {
        // 新規登録時は一覧に戻る
        router.push({
          name: 'BoundaryList',
          query: {
            screen: props.mode, // モードをクエリストリングに付与
          },
        })
      }
      addMessageQueue(
        MESSAGE_TYPE_TOAST,
        MessageService.bind(MessageService.codes.SAVE_COMPLETE, { target: '境界立会申請' })
      )
    }

    return {
      form,
      formRef,
      mapCenterLat,
      mapCenterLng,
      mapZoom,
      mapLoad,
      mapCollapseChange: () => {
        // MapのCollapseが変化するとmap読み込みを有効化する
        mapLoad.value = true
      },
      changeMapCenter,
      fileRefs,
      generalAttachedFilesRef,
      boundary_application_form_file_ref,
      identification_file_ref,
      power_of_attorney_file_ref,
      location_map_file_ref,
      ground_plan_file_ref,
      survey_drawing_of_acreage_file_ref,
      cadastral_map_file_ref,
      certificate_of_registered_matters_file_ref,
      actual_place_photo_file_ref,
      validationError,
      isEdit,
      returnTo,
      isEditBtnVisible,
      isApplicantContactBtnVisible,
      isMessageHistoryVisible,
      rules: boundaryEditRule,
      save,
      saveSent,
      saveConfirmDialogVisible,
      MessageTypes,
      FileTypes,
      allowExts: APPLICANT_UPLOAD_FILE_EXTS,
      RequestReasonTypes,
      HISTORY_MESSAGE_TYPES,
      sumpleSubmissionsUrl: process.env.VUE_APP_BOUNDARY_SUMPLE_SUBMISSIONS_URL,
    }
  },
})
